const getCartIdFromCookie = () => {
  const cartCookie = getCookie("cart");
  if (cartCookie === null || cartCookie === undefined) return null;
  const decoded = decodeURIComponent(cartCookie);
  if (decoded === null || decoded === undefined) return null;
  const [cartId] = decoded.split("?");
  if (cartId === null || cartId === undefined) return null;
  return cartId;
};

async function getCartItems() {
  try {
    const res = await fetch("/cart.js");
    if (!res.ok) {
      throw new Error(`Cart API Error: ${res.status}`);
    }
    const cart = await res.json();
    return cart?.items?.length ? cart.items : null;
  } catch (err) {
    console.error("Error fetching cart:", err);
    captureErrorWithScope(err, {
      area: "cart",
      method: "getCartItems",
    });
    return null;
  }
}

async function getItemCount() {
  try {
    const res = await fetch("/cart.js");
    if (!res.ok) {
      throw new Error(`Cart API Error: ${res.status}`);
    }
    const cart = await res.json();
    return cart?.items?.length ? cart.item_count : 0;
  } catch (err) {
    console.error("Error fetching cart:", err);
    captureErrorWithScope(err, {
      area: "cart",
      method: "getItemCount",
    });
    return 0;
  }
}

function updateCartCount(count) {
  const cartLink = document.getElementById("cart-icon-bubble");
  let bubble = document.querySelector("#cart-icon-bubble .cart-count-bubble");
  if (!cartLink) {
    toggleCartLoader("hide");
    return;
  }
  if (count > 0) {
    cartLink.classList.remove("fp-cart-empty__icon");
    if (!bubble) {
      bubble = document.createElement("div");
      bubble.className = "cart-count-bubble body-sm fp-font-weight--medium fp-cart-count__single-digit";
      const itemCount = document.createElement("span");
      itemCount.setAttribute("aria-hidden", "true");

      bubble.appendChild(itemCount);
      cartLink.appendChild(bubble);
    }
    bubble.querySelector('span[aria-hidden="true"]').textContent = String(count);
  } else {
    if (bubble) {
      bubble.remove();
    }
    cartLink.classList.add("fp-cart-empty__icon");
  }
  if (window.location.pathname === "/cart") {
    window.location.reload();
  }
  toggleCartLoader("hide");
}

async function addCartItems(items, successCallback, failCallback, shouldUpdateCount = true) {
  toggleCartLoader("show");
  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items,
      }),
    });
    if (!response.ok) {
      if (failCallback) failCallback();
      throw new Error("Failed to add items to cart");
    } else {
      const data = await response?.json();
      if (successCallback) successCallback(data);
    }

    if (shouldUpdateCount) {
      if (window.location.pathname === "/cart") {
        window.location.reload();
      }
      const cartCount = await getItemCount();
      updateCartCount(cartCount);
      return;
    } else {
      toggleCartLoader("hide");
    }
  } catch (err) {
    console.error(`Error adding to cart:`, err);
    captureErrorWithScope(err, {
      area: "cart",
      method: "addCartItems",
    });
    toggleCartLoader("hide");
  }
}

async function updateCartItemQuantity(url, quantity = 0, successCallback, failCallback, shouldUpdateCount = true) {
  toggleCartLoader("show");
  if (!url) {
    toggleCartLoader("hide");
    return;
  }
  const removeUrl = `${url}&quantity=${quantity}`;
  try {
    const data = await fetch(removeUrl);
    if (data.status && data.status !== 200) {
      if (failCallback) failCallback();
      throw new Error(data.description || "Failed to remove items from cart");
    } else {
      if (successCallback) successCallback(data);
    }

    if (shouldUpdateCount) {
      if (window.location.pathname === "/cart") {
        window.location.reload();
      }

      const cartCount = data?.item_count || 0;
      updateCartCount(cartCount);
      return;
    } else {
      toggleCartLoader("hide");
    }
  } catch (err) {
    console.error("Error removing item from cart:", err);
    toggleCartLoader("hide");
  }
}

async function updateCartItemsQuantity(updateObject, successCallback, failCallback, shouldUpdateCount = true) {
  toggleCartLoader("show");
  if (!updateObject) {
    toggleCartLoader("hide");
    return;
  }

  try {
    const data = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: updateObject,
      }),
    });
    if (data.status && data.status !== 200) {
      if (failCallback) failCallback();
      throw new Error(data.description || "Failed to update items in cart");
    } else {
      if (successCallback) successCallback(data);
    }

    if (shouldUpdateCount) {
      if (window.location.pathname === "/cart") {
        window.location.reload();
      }

      const cartCount = data?.item_count || 0;
      updateCartCount(cartCount);
      return;
    } else {
      toggleCartLoader("hide");
    }
  } catch (err) {
    console.error("Error updating cart items:", err);
    toggleCartLoader("hide");
  }
}

async function clearCartAttributes() {
  try {
    const data = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        attributes: {
          layaway_deposit: "",
          product_original_price: "",
          is_reserve: "",
        },
      }),
    });
    return data;
  } catch (error) {
    console.error("Error clearing cart:", error);
  }
}

async function syncCart(cartCookie, customerCartCookie, guestCart, token, wasPersistentCartProcessed, isReserveCart) {
  if (isReserveCart) {
    await clearCartAttributes();
  }
  if (cartCookie === customerCartCookie) {
    toggleCartLoader("hide");
    return;
  }
  if (cartCookie && !customerCartCookie) {
    try {
      await fetch(`${window.webAppBase}/persistent-cart`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ cartCookie }),
      });
      if (wasPersistentCartProcessed) {
        toggleCartLoader("hide");
        return;
      }
    } catch (error) {
      toggleCartLoader("hide");
      console.error("Error syncing cart:", error);
    }
  } else {
    setCookie("cart", decodeURIComponent(customerCartCookie), 365, location.hostname);
    if (!guestCart || guestCart.length === 0) {
      try {
        const cartCount = await getItemCount();
        updateCartCount(cartCount);
      } catch (error) {
        console.error("Error syncing cart:", error);
      } finally {
        if (window.location.pathname === "/cart") {
          window.location.reload();
        }
        toggleCartLoader("hide");
      }
    } else {
      try {
        const userCartItems = await getCartItems();
        const itemsToAdd = [];
        guestCart.forEach((guestItem) => {
          const matchingItem = userCartItems?.find(
            (userItem) =>
              userItem.product_id === guestItem.product_id &&
              JSON.stringify(userItem.properties) === JSON.stringify(guestItem.properties),
          );
          if (matchingItem) {
            const quantityDifference = guestItem.quantity - matchingItem.quantity;
            if (quantityDifference > 0) {
              itemsToAdd.push({ ...guestItem, quantity: quantityDifference });
            }
          } else {
            itemsToAdd.push(guestItem);
          }
        });
        await addCartItems(itemsToAdd);
      } catch (error) {
        toggleCartLoader("hide");
        console.error("Error adding guest cart items:", error);
      }
    }
  }
}

function writeCache(key, data) {
  sessionStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), data }));
}

function readCache(key) {
  const TTL = 5 * 60 * 1000;
  try {
    const cachedSearches = sessionStorage.getItem(key);
    if (!cachedSearches) {
      return null;
    }
    const { createdAt, data } = JSON.parse(cachedSearches);
    if (Date.now() - createdAt > TTL) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch (error) {
    sessionStorage.removeItem(key);
    return null;
  }
}

function invalidateAllSavedSearches() {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith("saved-searches")) {
      sessionStorage.removeItem(key);
    }
  });
}

/** @returns {"UK" | "US"} */
function getSavedSearchMarket() {
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith(".co.uk")) {
    return "UK";
  }
  return "US";
}

async function fetchSavedSearches(page = 1, { forceRefresh = false } = {}) {
  const market = getSavedSearchMarket();
  const key = `saved-searches-${market.toLowerCase()}-page-${page}`;
  const isLoggedIn = window.isLoggedIn || false;
  if (!isLoggedIn) {
    return { data: [] };
  }
  const token = await window.getSessionToken();
  if (!token) {
    return { data: [] };
  }
  const base = window.webAppBase;
  if (!base) {
    return { data: [] };
  }
  if (!forceRefresh) {
    const cached = readCache(key + `?page=${page}`);
    if (cached) {
      return cached;
    }
  }
  try {
    const params = new URLSearchParams({
      pageSize: "50",
      page: String(page),
      market,
    });
    const res = await fetch(`${base}/saved-search?${params}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json();
    writeCache(key + `?page=${page}`, data);
    return data;
  } catch (err) {
    console.error("getSavedSearches", err);
    return { data: [] };
  }
}

/**
 * Detects whether the current browser is iOS (iPhone, iPad, iPod, or iPadOS)
 * or Safari on macOS desktop.
 *
 * This function checks:
 * - `navigator.platform` for iOS device identifiers.
 * - The special case of iPadOS 13+ (which reports as "Mac" in the UA string but supports touch events).
 * - Safari desktop by inspecting the user agent string, while excluding Chromium-based browsers.
 *
 * @function iOSOrSafari
 * @returns {boolean} `true` if the device is iOS/iPadOS or the browser is Safari on macOS desktop, otherwise `false`.
 *
 * @example
 * if (iOSOrSafari()) {
 *   console.log("Running on iOS or Safari desktop");
 * }
 */
function iOSOrSafari() {
  const ua = navigator.userAgent;

  const isIOSDevice = ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
    navigator.platform,
  );

  // iPadOS 13+ reports "Mac" but has touch support
  const isIPadOS = ua.includes("Mac") && "ontouchend" in document;

  // Detect Safari desktop (exclude Chrome, Edge, Firefox which also use WebKit base UA parts)
  const isSafariDesktop =
    ua.includes("Safari") && !ua.includes("Chrome") && !ua.includes("Chromium") && !ua.includes("Edg");

  return isIOSDevice || isIPadOS || isSafariDesktop;
}
/**
 * Creates a throttled version of a function that ensures the callback
 * is invoked at most once per animation frame, using the latest arguments.
 *
 * Useful for performance-heavy events like scroll, resize, or mousemove
 * where smooth UI updates are desired.
 *
 * @param {Function} callback - The function to throttle.
 * @returns {Function} A throttled function that syncs execution with requestAnimationFrame.
 */
function smoothThrottle(callback) {
  let isThrottled = false;
  let lastArgs = null;
  let lastContext = null;

  return function (...args) {
    lastArgs = args;
    lastContext = this;

    if (!isThrottled) {
      isThrottled = true;
      requestAnimationFrame(() => {
        callback.apply(lastContext, lastArgs);
        isThrottled = false;
      });
    }
  };
}

function getLoginUrl(baseHref) {
  const base = baseHref || window.accountLoginUrl;
  const url = new URL(base, window.location.origin);
  url.pathname = url.pathname.replace("/redirect", "/login");
  url.searchParams.set("signinaction", "1");

  let returnTo = decodeURIComponent(getCookie(getEnvScopedCookieName("return_to")) || "/");
  if (returnTo) {
    if (window.isSatellite) {
      if (!returnTo.includes("__clerk_synced=false")) {
        const separator = returnTo.includes("?") ? "&" : "?";
        returnTo = `${returnTo}${separator}__clerk_synced=false`;
      }
    }
    url.searchParams.set("return_to", returnTo);
  }
  return url.toString();
}

function updateLoginLinks() {
  const loginLinks = document.querySelectorAll('a[data-login-link="true"]');
  if (!loginLinks.length) {
    return;
  }
  loginLinks.forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) {
      return;
    }
    if (!href.includes("/login")) {
      return;
    }
    const url = new URL(href, window.location.origin);
    link.href = getLoginUrl(url);
  });
}

function segmentTrack({ eventName, payload } = {}) {
  if (!eventName) {
    console.error("segmentTrack: eventName is required");
    return;
  }

  if (window.analytics && typeof window.analytics.track === "function") {
    try {
      window.analytics.track(eventName, payload);
    } catch (error) {
      console.error(`Error tracking event ${eventName}:`, error);
    }
  } else {
    (window.analytics = window.analytics || []).push(["track", eventName, payload]);
  }
}

async function removeReservedItemFromCart(lastOrderId, lastOrderVariantId, cartItems) {
  if (!lastOrderId || !lastOrderVariantId || !cartItems?.length) {
    return false;
  }

  const removedReserveOrderId = getCookie("fp_reserve_removed") || null;
  if (removedReserveOrderId === String(lastOrderId)) {
    return false;
  }

  const reservedItem = cartItems.find((item) => item.variant_id === lastOrderVariantId);
  if (!reservedItem) {
    return false;
  }
  try {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: reservedItem.key,
        quantity: 0,
      }),
    });

    if (res.ok) {
      setCookie("fp_reserve_removed", lastOrderId, 30);
      return true;
    }
  } catch (error) {
    console.error("Error removing reserved item from cart:", error);
  }
  return false;
}
